import { useEffect, useState } from 'react';
import { deleteRecord, listRecords, putRecord, type StoredRecord } from '../../persistence/db';
import { exportSave, loadPlayerGame } from '../../persistence/saves';
import { replayConfig, validateReplay, type Replay } from '../../replay/replay';
import { loadSimulation, type SaveData } from '../../engine/simulation/simulation';
import { useApp } from '../store';

export function LoadScreen() {
  const screen = useApp((s) => s.screen);
  const [tab, setTab] = useState<'saves' | 'replays'>(screen === 'replays' ? 'replays' : 'saves');
  const [items, setItems] = useState<StoredRecord[]>([]);
  const [err, setErr] = useState('');
  const refresh = () => listRecords(tab).then(setItems).catch((e) => setErr(String(e?.message ?? e)));
  useEffect(() => {
    void refresh();
  }, [tab]);

  const open = async (r: StoredRecord) => {
    setErr('');
    try {
      if (tab === 'saves') {
        const sim = await loadPlayerGame(r.name);
        useApp.setState({ launch: { sim, saveName: r.name }, pendingConfig: null, screen: 'game', menuOpen: false });
      } else {
        const rep = JSON.parse(r.data) as Replay;
        const bad = validateReplay(rep);
        if (bad) throw new Error(bad);
        useApp.setState({ launch: { replay: rep.commands, replayEnd: rep.finalTick }, pendingConfig: replayConfig(rep), screen: 'game', menuOpen: false });
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const importFile = async (f: File) => {
    setErr('');
    try {
      const text = await f.text();
      const obj = JSON.parse(text);
      if (obj.saveVersion !== undefined) {
        loadSimulation(obj as SaveData); // validates
        await putRecord('saves', { name: `Imported ${f.name}`, date: Date.now(), meta: { imported: true }, data: text });
        setTab('saves');
      } else if (obj.replayVersion !== undefined) {
        const bad = validateReplay(obj as Replay);
        if (bad) throw new Error(bad);
        await putRecord('replays', { name: `Imported ${f.name}`, date: Date.now(), meta: { imported: true }, data: text });
        setTab('replays');
      } else throw new Error('Unrecognised file.');
      void refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="menu-bg">
      <div className="menu-card" style={{ maxWidth: 760 }}>
        <div className="row spread"><h2>{tab === 'saves' ? 'Load Game' : 'Replays'}</h2><button className="btn" onClick={() => useApp.getState().setScreen('menu')}>Back</button></div>
        <div className="tabs">
          <button className={`tab ${tab === 'saves' ? 'active' : ''}`} onClick={() => setTab('saves')}>Saved games</button>
          <button className={`tab ${tab === 'replays' ? 'active' : ''}`} onClick={() => setTab('replays')}>Replays</button>
          <label className="tab" style={{ marginLeft: 'auto' }}>Import file<input type="file" accept=".json" style={{ display: 'none' }} onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} /></label>
        </div>
        {err && <p style={{ color: 'var(--danger)' }}>{err}</p>}
        <div className="list">
          {items.length === 0 && <div className="list-item muted">Nothing here yet.</div>}
          {items.map((r) => (
            <div key={r.name} className="list-item">
              <div>
                <div>{r.name}</div>
                <div className="small muted">{Object.entries(r.meta).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>
              </div>
              <div className="row">
                <button className="btn primary" onClick={() => open(r)}>{tab === 'saves' ? 'Load' : 'Watch'}</button>
                <button className="btn" onClick={() => exportSave(r)}>Export</button>
                <button className="btn danger" onClick={() => deleteRecord(tab, r.name).then(refresh)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
